import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FilterOptions from './components/FilterOptions';
import { getApiErrorMessage, getApiErrorStatus } from '../../api';
import { checkinService, type CheckinHistory } from '../../api/checkin';
import { fuelingHistoryService, type FuelingHistory } from '../../api/fueling.history';
import { historyTripService, type HistoryTrip } from '../../api/history.trip';
import { incidentService } from '../../api/incidents';
import { maintenanceService } from '../../api/maintenance';
import { tripService } from '../../api/trips';
import { useAuth } from '../../context/AuthContext';
import {
  Car,
  Route,
  Wrench,
  TriangleAlert,
  Fuel,
  Search,
  Filter as FilterIcon,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  PageHeader,
  StatCard,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Badge,
  Button,
} from '../../components/ui/SharedLayout';

interface VeiculoQuilometragem {
  id: string;
  placa: string;
  modelo: string;
  quilometragemAtual: number;
  quilometragemMensal: number;
  mediaConsumo: number;
  ultimaAtualizacao: string;
}

interface Activity {
  id: string;
  data: string;
  veiculo: string;
  tipo: 'Check-in' | 'Abastecimento' | 'Viagem';
  status: string;
  responsavel: string;
  created_at: string;
}

interface ClientDashboardData {
  vehiclesWithActivity: number;
  trips: number;
  pendingTrips: number;
  tripsInProgress: number;
  incidents: number;
  fuelings: number;
  totalFuelCost: number;
  activeMaintenances: number;
}

type ActivityTone = 'green' | 'amber' | 'blue';

const normalizeActivityStatus = (status?: string): { label: string; tone: ActivityTone } => {
  const normalized = (status || '').toLowerCase();

  if (
    normalized.includes('final') ||
    normalized.includes('conclu') ||
    normalized.includes('arquiv')
  ) {
    return {
      label: status || 'Concluído',
      tone: 'green',
    };
  }

  if (
    normalized.includes('pend') ||
    normalized.includes('blocked') ||
    normalized.includes('iniciado')
  ) {
    return {
      label: status || 'Pendente',
      tone: 'amber',
    };
  }

  return {
    label: status || 'Em andamento',
    tone: 'blue',
  };
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);

const getVehicleDisplayName = (
  vehicle?: { description?: string; brand?: string; license_plate?: string } | null,
  fallbackId?: number,
) => {
  if (vehicle?.description && vehicle?.license_plate) {
    return `${vehicle.description} - ${vehicle.license_plate}`;
  }

  if (vehicle?.brand && vehicle?.license_plate) {
    return `${vehicle.brand} - ${vehicle.license_plate}`;
  }

  if (vehicle?.license_plate) {
    return vehicle.license_plate;
  }

  return fallbackId ? `Veículo #${fallbackId}` : '-';
};

const buildMileageRows = (fuelingHistory: FuelingHistory[]): VeiculoQuilometragem[] => {
  const grouped = new Map<number, FuelingHistory[]>();

  fuelingHistory.forEach((item) => {
    if (!grouped.has(item.vehicle_id)) {
      grouped.set(item.vehicle_id, []);
    }
    grouped.get(item.vehicle_id)?.push(item);
  });

  const last30Days = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

  return Array.from(grouped.entries())
    .map(([vehicleId, items]) => {
      const orderedItems = [...items].sort(
        (a, b) =>
          new Date(a.fueling_time || a.created_at).getTime() -
          new Date(b.fueling_time || b.created_at).getTime(),
      );
      const lastItem = orderedItems[orderedItems.length - 1];
      const odometerItems = orderedItems.filter(
        (item) => typeof item.odometer_reading === 'number',
      );
      const recentItems = orderedItems.filter(
        (item) => new Date(item.fueling_time || item.created_at) >= last30Days,
      );
      const recentOdometerItems = recentItems.filter(
        (item) => typeof item.odometer_reading === 'number',
      );

      const firstRecentOdometer = recentOdometerItems[0];
      const lastRecentOdometer =
        recentOdometerItems[recentOdometerItems.length - 1];
      const quilometragemMensal =
        firstRecentOdometer?.odometer_reading != null &&
        lastRecentOdometer?.odometer_reading != null
          ? Math.max(
              0,
              lastRecentOdometer.odometer_reading - firstRecentOdometer.odometer_reading,
            )
          : 0;
      const totalLitros = recentItems.reduce(
        (sum, item) => sum + (Number(item.amount) || 0),
        0,
      );

      return {
        id: String(vehicleId),
        placa: lastItem?.vehicle?.license_plate || `#${vehicleId}`,
        modelo:
          lastItem?.vehicle?.description ||
          lastItem?.vehicle?.brand ||
          `Veículo #${vehicleId}`,
        quilometragemAtual:
          odometerItems[odometerItems.length - 1]?.odometer_reading || 0,
        quilometragemMensal,
        mediaConsumo: totalLitros > 0 ? quilometragemMensal / totalLitros : 0,
        ultimaAtualizacao: new Date(
          lastItem?.fueling_time || lastItem?.created_at || Date.now(),
        ).toLocaleDateString('pt-BR'),
      };
    })
    .sort((a, b) => a.placa.localeCompare(b.placa, 'pt-BR'));
};

const isForbiddenScopeError = (error: unknown) => getApiErrorStatus(error) === 403;

const Dashboard = () => {
  const { user } = useAuth();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>({});
  const [dados, setDados] = useState<ClientDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summaryWarning, setSummaryWarning] = useState<string | null>(null);
  const [dashboardNotice, setDashboardNotice] = useState<string | null>(null);

  const [atividades, setAtividades] = useState<Activity[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [activitiesError, setActivitiesError] = useState<string | null>(null);

  const [quilometragemVeiculos, setQuilometragemVeiculos] = useState<
    VeiculoQuilometragem[]
  >([]);
  const [quilometragemLoading, setQuilometragemLoading] = useState(true);
  const [quilometragemError, setQuilometragemError] = useState<string | null>(null);
  const [quilometragemSearchTerm, setQuilometragemSearchTerm] = useState('');

  const statsCarouselRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = useCallback(() => {
    const el = statsCarouselRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < maxScroll - 2);
  }, []);

  const handleScrollStats = useCallback((direction: 'left' | 'right') => {
    if (!statsCarouselRef.current) return;
    const container = statsCarouselRef.current;
    const cardWidth = container.clientWidth * 0.92;
    container.scrollBy({
      left: direction === 'left' ? -cardWidth : cardWidth,
      behavior: 'smooth',
    });
    setTimeout(updateScrollButtons, 550);
  }, [updateScrollButtons]);

  useEffect(() => {
    const el = statsCarouselRef.current;
    if (!el) return;
    const run = () => updateScrollButtons();
    run();
    requestAnimationFrame(() => {
      run();
      setTimeout(run, 120);
    });
    const onScroll = () => run();
    const onResize = () => run();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    const t = setTimeout(run, 260);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      clearTimeout(t);
    };
  }, [updateScrollButtons, loading, dados]);

  const filterCategories = useMemo(
    () => ({
      Tipo: [
        { id: 'Check-in', label: 'Check-in' },
        { id: 'Abastecimento', label: 'Abastecimento' },
        { id: 'Viagem', label: 'Viagem' },
      ],
      Status: [
        { id: 'Concluído', label: 'Concluído' },
        { id: 'Pendente', label: 'Pendente' },
        { id: 'Em andamento', label: 'Em andamento' },
      ],
    }),
    [],
  );

  const filteredActivities = useMemo(() => {
    return atividades.filter((atividade) => {
      const typeFilters = appliedFilters.Tipo || [];
      const statusFilters = appliedFilters.Status || [];
      const normalizedStatus = normalizeActivityStatus(atividade.status).label;

      const matchesType =
        typeFilters.length === 0 || typeFilters.includes(atividade.tipo);
      const matchesStatus =
        statusFilters.length === 0 || statusFilters.includes(normalizedStatus);

      return matchesType && matchesStatus;
    });
  }, [appliedFilters, atividades]);

  const filteredMileageRows = useMemo(() => {
    if (!quilometragemSearchTerm) {
      return quilometragemVeiculos;
    }

    const term = quilometragemSearchTerm.toLowerCase();
    return quilometragemVeiculos.filter(
      (veiculo) =>
        veiculo.placa.toLowerCase().includes(term) ||
        veiculo.modelo.toLowerCase().includes(term),
    );
  }, [quilometragemSearchTerm, quilometragemVeiculos]);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSummaryWarning(null);
    setDashboardNotice(null);
    setQuilometragemLoading(true);
    setQuilometragemError(null);

    try {
      const results = await Promise.allSettled([
        tripService.getAll(),
        maintenanceService.getAll('active'),
        incidentService.getAll(),
        fuelingHistoryService.getAllFuelingHistory(),
      ]);

      const [tripsResult, maintenancesResult, incidentsResult, fuelingResult] = results;
      const trips = tripsResult.status === 'fulfilled' ? tripsResult.value : [];
      const maintenances =
        maintenancesResult.status === 'fulfilled' ? maintenancesResult.value : [];
      const incidents =
        incidentsResult.status === 'fulfilled' ? incidentsResult.value : [];
      const fuelingHistory =
        fuelingResult.status === 'fulfilled' ? fuelingResult.value : [];

      if (results.every((result) => result.status === 'rejected')) {
        const rejectedReasons = results
          .filter(
            (result): result is PromiseRejectedResult => result.status === 'rejected',
          )
          .map((result) => result.reason);

        if (rejectedReasons.every((reason) => isForbiddenScopeError(reason))) {
          const hasDepartmentReference = Boolean(
            (user as { departamentName?: string | null; managedDepartamentName?: string | null })
              ?.departamentName ||
              (user as { departamentName?: string | null; managedDepartamentName?: string | null })
                ?.managedDepartamentName,
          );

          setDados({
            vehiclesWithActivity: 0,
            trips: 0,
            pendingTrips: 0,
            tripsInProgress: 0,
            incidents: 0,
            fuelings: 0,
            totalFuelCost: 0,
            activeMaintenances: 0,
          });
          setDashboardNotice(
            hasDepartmentReference
              ? 'Seu setor ainda não possui dados suficientes para montar os indicadores do dashboard.'
              : 'Seu perfil cliente ainda não possui um departamento vinculado. Sem esse vínculo, o dashboard não consegue carregar dados do setor.',
          );
          setQuilometragemVeiculos([]);
          setQuilometragemError(
            hasDepartmentReference
              ? 'A quilometragem será exibida quando houver abastecimentos ou veículos no setor.'
              : 'A quilometragem depende de um departamento vinculado ao seu perfil cliente.',
          );
          return;
        }

        setError(
          getApiErrorMessage(
            rejectedReasons[0],
            'Erro ao carregar dados do dashboard.',
          ),
        );
        setDados(null);
        setQuilometragemVeiculos([]);
        return;
      }

      const vehicleIds = new Set<number>();
      trips.forEach((trip) => vehicleIds.add(trip.vehicle_id));
      maintenances.forEach((maintenance) => vehicleIds.add(maintenance.vehicle_id));
      incidents.forEach((incident) => vehicleIds.add(incident.vehicle_id));
      fuelingHistory.forEach((fueling) => vehicleIds.add(fueling.vehicle_id));

      setDados({
        vehiclesWithActivity: vehicleIds.size,
        trips: trips.length,
        pendingTrips: trips.filter((trip) => trip.status === 'pendente').length,
        tripsInProgress: trips.filter((trip) => trip.status === 'em_andamento').length,
        incidents: incidents.length,
        fuelings: fuelingHistory.length,
        totalFuelCost: fuelingHistory.reduce(
          (sum, fueling) => sum + (Number(fueling.total_cost) || 0),
          0,
        ),
        activeMaintenances: maintenances.length,
      });

      if (fuelingResult.status === 'fulfilled') {
        setQuilometragemVeiculos(buildMileageRows(fuelingHistory));
      } else {
        setQuilometragemVeiculos([]);
        setQuilometragemError(
          'A quilometragem não pode ser calculada sem o histórico de abastecimento.',
        );
      }

      const failedRequests = results.filter((result) => result.status === 'rejected').length;
      if (failedRequests > 0) {
        setSummaryWarning(
          'Alguns indicadores foram calculados parcialmente porque nem todas as fontes responderam.',
        );
      }

      if (
        trips.length === 0 &&
        maintenances.length === 0 &&
        incidents.length === 0 &&
        fuelingHistory.length === 0
      ) {
        setDashboardNotice(
          'Ainda não há movimentações registradas para o seu setor. Os indicadores serão exibidos assim que houver viagens, abastecimentos, manutenções ou incidentes.',
        );
      }
    } catch (_error) {
      setError(getApiErrorMessage(_error, 'Erro ao carregar dados do dashboard.'));
      setDados(null);
      setQuilometragemVeiculos([]);
    } finally {
      setLoading(false);
      setQuilometragemLoading(false);
    }
  }, [user]);

  const fetchRecentActivities = useCallback(async () => {
    setActivitiesLoading(true);
    setActivitiesError(null);

    try {
      const results = await Promise.allSettled([
        checkinService.getHistory(),
        fuelingHistoryService.getAllFuelingHistory(),
        historyTripService.getAll(),
      ]);

      const [checkinResult, fuelingResult, tripResult] = results;
      const activities: Activity[] = [];

      const checkinHistory =
        checkinResult.status === 'fulfilled' ? checkinResult.value : [];
      const fuelingHistory =
        fuelingResult.status === 'fulfilled' ? fuelingResult.value : [];
      const tripHistory = tripResult.status === 'fulfilled' ? tripResult.value : [];

      checkinHistory.forEach((item: CheckinHistory) => {
        activities.push({
          id: `checkin-${item.id}`,
          data: item.created_at
            ? new Date(item.created_at).toLocaleDateString('pt-BR')
            : '',
          veiculo: getVehicleDisplayName(item.checkin?.vehicle),
          tipo: 'Check-in',
          status: item.action || '-',
          responsavel: item.checkin?.employee?.name || '-',
          created_at: item.created_at,
        });
      });

      fuelingHistory.forEach((item: FuelingHistory) => {
        activities.push({
          id: `fueling-${item.id}`,
          data: item.created_at
            ? new Date(item.created_at).toLocaleDateString('pt-BR')
            : '',
          veiculo: getVehicleDisplayName(item.vehicle, item.vehicle_id),
          tipo: 'Abastecimento',
          status: item.status || item.action || '-',
          responsavel: item.employee?.name || '-',
          created_at: item.created_at,
        });
      });

      tripHistory.forEach((item: HistoryTrip) => {
        activities.push({
          id: `trip-${item.id}`,
          data: item.created_at
            ? new Date(item.created_at).toLocaleDateString('pt-BR')
            : '',
          veiculo: item.vehicle_info?.replace('\n', ' - ') || '-',
          tipo: 'Viagem',
          status: item.action || '-',
          responsavel: item.employee_info?.replace('\n', ' - ') || '-',
          created_at: item.created_at,
        });
      });

      activities.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
      setAtividades(activities.slice(0, 20));

      const failedRequests = results.filter((result) => result.status === 'rejected').length;
      if (failedRequests === results.length) {
        const rejectedReasons = results
          .filter(
            (result): result is PromiseRejectedResult => result.status === 'rejected',
          )
          .map((result) => result.reason);

        if (rejectedReasons.every((reason) => isForbiddenScopeError(reason))) {
          setAtividades([]);
          setActivitiesError(null);
        } else {
          setActivitiesError(
            getApiErrorMessage(
              rejectedReasons[0],
              'Erro ao carregar atividades recentes.',
            ),
          );
        }
      } else if (failedRequests > 0) {
        setActivitiesError('Algumas atividades recentes não puderam ser carregadas.');
      }
    } catch (_error) {
      setActivitiesError(getApiErrorMessage(_error, 'Erro ao carregar atividades recentes.'));
    } finally {
      setActivitiesLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDashboardData();
    void fetchRecentActivities();
  }, [fetchRecentActivities, loadDashboardData]);

  const handleApplyFilter = useCallback((selectedFilters: Record<string, string[]>) => {
    setAppliedFilters(selectedFilters);
    setIsFilterOpen(false);
  }, []);

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <div className="flex h-64 items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <div
          className="rounded-xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700"
          role="alert"
        >
          <strong className="font-semibold">Erro.</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      </div>
    );
  }

  if (!dados) {
    return (
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <div
          className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800"
          role="alert"
        >
          <strong className="font-semibold">Atenção.</strong>
          <span className="block sm:inline">
            {' '}
            Nenhum dado disponível para exibição.
          </span>
        </div>
      </div>
    );
  }

  const activeFilterCount = Object.values(appliedFilters).flat().length;

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader
          title="Dashboard"
          description="Visão geral baseada em viagens, manutenções, incidentes e históricos permitidos para o perfil cliente."
          hideTitleInBody
          actions={
            <Badge tone="blue" className="!rounded-xl !px-3.5 !py-1.5 !text-[12px]">
              Perfil cliente — indicadores derivados de dados do seu setor
            </Badge>
          }
        />

        {dashboardNotice && (
          <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 px-5 py-4 text-sm text-blue-800">
            {dashboardNotice}
          </div>
        )}

        {summaryWarning && (
          <div className="mb-6 rounded-xl border border-sky-200 bg-sky-50 px-5 py-4 text-sm text-sky-800">
            {summaryWarning}
          </div>
        )}

        <div className="group/carousel relative mt-6">
          {canScrollLeft ? (
            <button
              type="button"
              onClick={() => handleScrollStats('left')}
              aria-label="Ver cards anteriores"
              className="pointer-events-auto absolute left-[-14px] top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 -translate-x-3 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 opacity-0 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.35)] transition-all duration-200 ease-out hover:border-blue-200 hover:text-blue-700 hover:scale-105 active:scale-95 group-hover/carousel:translate-x-0 group-hover/carousel:opacity-80 focus-visible:translate-x-0 focus-visible:opacity-90 sm:flex"
            >
              <ChevronLeft className="h-5 w-5 stroke-[2.2]" />
            </button>
          ) : null}

          <div
            ref={statsCarouselRef}
            className="flex w-full snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
          >
            <StatCard
              className="shrink-0 min-h-[180px] w-full snap-start md:w-[calc(50%-8px)] md:min-h-[190px] lg:w-[calc(33.33%-11px)] xl:w-[calc(25%-12px)] xl:min-h-[195px] 2xl:w-[calc(25%-12px)] 2xl:min-h-[200px]"
              icon={<Car className="h-5 w-5 stroke-[1.8]" />}
              label="Veículos com Atividade"
              value={dados.vehiclesWithActivity}
              trend={<span>Com registros no período carregado</span>}
              variant="primary"
            />

            <StatCard
              className="shrink-0 min-h-[180px] w-full snap-start md:w-[calc(50%-8px)] md:min-h-[190px] lg:w-[calc(33.33%-11px)] xl:w-[calc(25%-12px)] xl:min-h-[195px] 2xl:w-[calc(25%-12px)] 2xl:min-h-[200px]"
              icon={<Route className="h-5 w-5 stroke-[1.8]" />}
              label="Solicitações de Viagem"
              value={dados.trips}
              trend={
                <span className="inline-flex items-center gap-1.5 flex-wrap">
                  <span className="font-semibold">{dados.pendingTrips}</span>
                  <span>pendentes</span>
                  <span className="mx-0.5 text-white/60">·</span>
                  <span className="font-semibold">{dados.tripsInProgress}</span>
                  <span>em andamento</span>
                </span>
              }
              variant="deep"
            />

            <StatCard
              className="shrink-0 min-h-[180px] w-full snap-start md:w-[calc(50%-8px)] md:min-h-[190px] lg:w-[calc(33.33%-11px)] xl:w-[calc(25%-12px)] xl:min-h-[195px] 2xl:w-[calc(25%-12px)] 2xl:min-h-[200px]"
              icon={<Wrench className="h-5 w-5 stroke-[1.8]" />}
              label="Manutenções Ativas"
              value={dados.activeMaintenances}
              trend={<span>Registros em aberto no momento</span>}
              variant="rich"
            />

            <StatCard
              className="shrink-0 min-h-[180px] w-full snap-start md:w-[calc(50%-8px)] md:min-h-[190px] lg:w-[calc(33.33%-11px)] xl:w-[calc(25%-12px)] xl:min-h-[195px] 2xl:w-[calc(25%-12px)] 2xl:min-h-[200px]"
              icon={<TriangleAlert className="h-5 w-5 stroke-[1.8]" />}
              label="Incidentes"
              value={dados.incidents}
              trend={<span>Ocorrências registradas no seu setor</span>}
              variant="navy"
            />

            <StatCard
              className="shrink-0 min-h-[180px] w-full snap-start md:w-[calc(50%-8px)] md:min-h-[190px] lg:w-[calc(33.33%-11px)] xl:w-[calc(25%-12px)] xl:min-h-[195px] 2xl:w-[calc(25%-12px)] 2xl:min-h-[200px]"
              icon={<Fuel className="h-5 w-5 stroke-[1.8]" />}
              label="Histórico de Abastecimento"
              value={dados.fuelings}
              trend={
                <span className="inline-flex items-center gap-1.5 flex-wrap">
                  <span className="font-semibold">
                    {formatCurrency(dados.totalFuelCost)}
                  </span>
                  <span>de custo total</span>
                </span>
              }
              variant="sky"
            />
          </div>

          {canScrollRight ? (
            <button
              type="button"
              onClick={() => handleScrollStats('right')}
              aria-label="Ver próximos cards"
              className="pointer-events-auto absolute right-[-14px] top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 translate-x-3 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 opacity-0 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.35)] transition-all duration-200 ease-out hover:border-blue-200 hover:text-blue-700 hover:scale-105 active:scale-95 group-hover/carousel:translate-x-0 group-hover/carousel:opacity-80 focus-visible:translate-x-0 focus-visible:opacity-90 sm:flex"
            >
              <ChevronRight className="h-5 w-5 stroke-[2.2]" />
            </button>
          ) : null}
        </div>

        <SectionCard className="mt-7">
          <CardHeader>
            <div>
              <h2 className="text-sm font-semibold tracking-tight text-slate-900">
                Atividades Recentes
              </h2>
              <p className="mt-0.5 text-xs text-slate-500">
                Últimas movimentações de check-in, abastecimento e viagens
              </p>
            </div>
            <Button
              variant={activeFilterCount > 0 ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setIsFilterOpen(true)}
            >
              <FilterIcon className="h-3.5 w-3.5 stroke-[1.9]" />
              Filtros
              {activeFilterCount > 0 && (
                <span className="ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-md bg-white/25 px-1 text-[10px] font-semibold">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </CardHeader>
          <CardBody className="!px-0 !py-0">
            {activitiesLoading ? (
              <div className="py-10 text-center text-sm text-slate-500">
                Carregando atividades...
              </div>
            ) : activitiesError ? (
              <div className="py-10 text-center text-sm text-rose-600">{activitiesError}</div>
            ) : (
              <ModernTable>
                <Thead>
                  <Tr className="!hover:bg-transparent">
                    <Th>Data</Th>
                    <Th>Veículo</Th>
                    <Th>Tipo</Th>
                    <Th>Status</Th>
                    <Th>Responsável</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredActivities.length > 0 ? (
                    filteredActivities.map((atividade) => {
                      const status = normalizeActivityStatus(atividade.status);
                      return (
                        <Tr key={atividade.id}>
                          <Td className="font-medium text-slate-900">{atividade.data}</Td>
                          <Td>{atividade.veiculo}</Td>
                          <Td>
                            <Badge tone="slate">{atividade.tipo}</Badge>
                          </Td>
                          <Td>
                            <Badge tone={status.tone}>{status.label}</Badge>
                          </Td>
                          <Td>{atividade.responsavel}</Td>
                        </Tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="py-10 text-center text-sm text-slate-500">
                        Nenhuma atividade encontrada.
                      </td>
                    </tr>
                  )}
                </Tbody>
              </ModernTable>
            )}
          </CardBody>
        </SectionCard>

        <SectionCard className="mt-7">
          <CardHeader>
            <div>
              <h2 className="text-sm font-semibold tracking-tight text-slate-900">
                Quilometragem dos Veículos
              </h2>
              <p className="mt-0.5 text-xs text-slate-500">
                Quilometragem atual, mensal e média de consumo por veículo
              </p>
            </div>
            <div className="relative w-full sm:w-[320px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 stroke-[1.8] text-slate-400" />
              <Input
                type="text"
                placeholder="Pesquisar por placa ou modelo..."
                value={quilometragemSearchTerm}
                onChange={(e) => setQuilometragemSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </CardHeader>
          <CardBody className="!px-0 !py-0">
            {quilometragemLoading ? (
              <div className="py-10 text-center text-sm text-slate-500">
                Carregando quilometragem...
              </div>
            ) : quilometragemError ? (
              <div className="py-10 text-center text-sm text-rose-600">{quilometragemError}</div>
            ) : (
              <ModernTable>
                <Thead>
                  <Tr className="!hover:bg-transparent">
                    <Th>Placa</Th>
                    <Th>Modelo</Th>
                    <Th>Quilometragem Atual</Th>
                    <Th>Quilometragem Mensal</Th>
                    <Th>Média de Consumo</Th>
                    <Th>Última Atualização</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredMileageRows.length > 0 ? (
                    filteredMileageRows.map((veiculo) => (
                      <Tr key={veiculo.id}>
                        <Td>
                          <span className="font-semibold tracking-tight text-slate-900">
                            {veiculo.placa}
                          </span>
                        </Td>
                        <Td>{veiculo.modelo}</Td>
                        <Td className="font-semibold tabular-nums text-slate-900">
                          {veiculo.quilometragemAtual.toLocaleString('pt-BR')} km
                        </Td>
                        <Td className="tabular-nums">
                          {veiculo.quilometragemMensal.toLocaleString('pt-BR')} km
                        </Td>
                        <Td className="tabular-nums">{veiculo.mediaConsumo.toFixed(1)} km/L</Td>
                        <Td className="text-slate-500">{veiculo.ultimaAtualizacao}</Td>
                      </Tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="py-10 text-center text-sm text-slate-500">
                        Nenhum veículo encontrado.
                      </td>
                    </tr>
                  )}
                </Tbody>
              </ModernTable>
            )}
          </CardBody>
        </SectionCard>

        <FilterOptions
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApplyFilter={handleApplyFilter}
          filterCategories={filterCategories}
          initialFilters={appliedFilters}
        />
      </div>
    </div>
  );
};

export default Dashboard;
